import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { SIDEBAR_INPUT, SidebarComponent, SidebarInput } from './sidebar.component';
import { Observable, Observer, Subject } from 'rxjs';

export const SIDEBAR_DATA = new InjectionToken('sidebarData');

export type SidebarCloseRequest<R> = {
	result: R | undefined;
	/** Observer om true of false in te emitten om het sluiten wel of niet door te laten gaan. */
	allowClose: Observer<boolean>;
};

export type SidebarConfig<D = unknown> = {
	/** De data die meegegeven wordt aan de sidebar. */
	data?: D | null;
	/** Geeft aan of er een backdrop achter de sidebar moet worden weergegeven (default true). Zo ja, kan de gebruiker de sidebar sluiten door op de backdrop te klikken. */
	hasBackdrop?: boolean;
	/** Geeft aan of er meerdere sidebars van het meegegeven component tegelijk geopend kunnen worden (default true). */
	allowMultiple?: boolean;
};

export class SidebarRef<R = undefined> {
	constructor(
		public overlayRef: OverlayRef,
		public componentType: ComponentType<any>
	) {}
	readonly closed = new Subject<R | undefined>();
	private readonly closeRequestedSubject = new Subject<SidebarCloseRequest<R>>();

	/**
	 * Registreert een subscriber die een closeRequest afhandelt. De subscriber moet een true of een false emitten in de
	 * allowClose-observer om het sluiten wel of niet door te laten gaan.
	 */
	closeRequested(): Observable<SidebarCloseRequest<R>> {
		this.closeRequestSubscriberCount++;
		return this.closeRequestedSubject.asObservable();
	}

	private closeRequestSubscriberCount = 0;

	/**
	 * Vraagt de sidebar om te sluiten. Als er subscribers zijn die een closeRequest afhandelen, dan worden deze aangeroepen.
	 * Als alle subscribers een true emitten, dan wordt de sidebar gesloten met de meegegeven result.
	 * Als er überhaupt geen subscribers zijn kunnen we direct sluiten.
	 */
	requestClose(result?: R) {
		if (this.closeRequestSubscriberCount == 0) {
			this.close(result);
		}

		const allowClose = new Subject<boolean>();
		const responses: boolean[] = [];
		const subscription = allowClose.subscribe((allow) => {
			responses.push(allow);
			// Als alle subscribers (asynchroon) antwoord hebben gegeven, en alle antwoorden zijn true, dan kunnen we sluiten.
			if (responses.length === this.closeRequestSubscriberCount) {
				if (responses.every(Boolean)) this.close(result);
				subscription.unsubscribe();
			}
		});
		this.closeRequestedSubject.next({ result, allowClose });
	}

	private close(result?: R) {
		this.closed.next(result);
		this.closed.complete();
		this.overlayRef.detach();
	}
}

/**
 * Service om sidebars te openen (op dezelfde manier als Dialog.open).
 */
@Injectable({
	providedIn: 'root',
})
export class SidebarService {
	private readonly overlay = inject(Overlay);

	/** Keeps track of the currently-open sidebars. */
	readonly openSidebars: SidebarRef<any>[] = [];

	/**
	 * Closes all currently open sidebars.
	 */
	closeAll() {
		// Create a copy since closing sidebars modifies the array
		const sidebarsToClose = [...this.openSidebars];
		sidebarsToClose.forEach((sidebar) => sidebar.requestClose());
	}

	/**
	 * Gets all open sidebars of a specific component type.
	 * @param componentType The component type to search for.
	 * @returns Array of sidebar references for the specified component type.
	 */
	getSidebarsByType<C>(componentType: ComponentType<C>): readonly SidebarRef<any>[] {
		return this.openSidebars.filter((sidebar) => sidebar.componentType === componentType);
	}

	open<R = unknown, D = unknown, C = unknown>(component: ComponentType<C>, config?: SidebarConfig<D>): SidebarRef<R> {
		const allowMultiple = config?.allowMultiple ?? true;
		if (!allowMultiple) {
			const existingSidebars: readonly SidebarRef<R>[] = this.getSidebarsByType(component);
			if (existingSidebars.length > 0) {
				return existingSidebars[0];
			}
		}

		const hasBackdrop = config?.hasBackdrop ?? true;
		const overlayRef = this.overlay.create({ panelClass: 'sidebar-overlay-pane', hasBackdrop });
		const sidebarRef = new SidebarRef<R>(overlayRef, component);

		// Add to tracking list
		this.openSidebars.push(sidebarRef);

		// Remove from tracking list when closed
		sidebarRef.closed.subscribe(() => {
			const index = this.openSidebars.indexOf(sidebarRef);
			if (index > -1) {
				this.openSidebars.splice(index, 1);
			}
		});

		// Injector waarmee het meegegeven component de data kan ophalen met @Inject(SIDEBAR_DATA) in de constructor of field = inject(SIDEBAR_DATA).
		// Ook de SidebarRef wordt meegegeven zodat het component de sidebar kan sluiten.
		const dataInjector = Injector.create({
			providers: [
				{ provide: SIDEBAR_DATA, useValue: config?.data },
				{ provide: SidebarRef, useValue: sidebarRef },
			],
		});

		// Het SidebarComponent krijgt het meegegeven component en de dataInjector mee als providers.
		const sidebarInput: SidebarInput<C> = { component, injector: dataInjector };
		const sidebarInjector = Injector.create({ providers: [{ provide: SIDEBAR_INPUT, useValue: sidebarInput }] });
		const componentPortal = new ComponentPortal(SidebarComponent, null, sidebarInjector);
		overlayRef.attach(componentPortal);
		if (hasBackdrop) {
			overlayRef.backdropClick().subscribe(() => sidebarRef.requestClose());
		}

		return sidebarRef;
	}
}
